import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useModulePermission } from '../../../hooks/usePermission';
import type { ApiError } from '../../../lib/errors';
import { baoCaoDoanhNghiepRepository as repo } from '../../../lib/repositories/bao-cao-doanh-nghiep.repository';
import { QUYEN } from '../constants';
import type { CreateSourceInput, Source, UpdateSourceInput } from '../types';
import { KHOA_BAO_CAO, thuLai } from './useBaoCao';

/** Tham số trên URL: `?nguon=<code>`. */
export const THAM_SO_NGUON = 'nguon';
/** Thư mục thuộc về nguồn — đổi nguồn thì bỏ luôn `?thuMuc=`. */
export const THAM_SO_THU_MUC = 'thuMuc';

export function useDanhSachNguon(bat = true) {
  return useQuery<Source[], ApiError>({
    queryKey: KHOA_BAO_CAO.nguon,
    queryFn: () => repo.nguon(),
    enabled: bat,
    staleTime: 30_000,
    retry: thuLai,
  });
}

export interface NguonDangXem {
  /** Mã nguồn gửi trong `?source=`; `undefined` là nguồn mặc định của server. */
  code: string | undefined;
  nguon: Source | undefined;
  danhSach: Source[];
  /** Đã biết nguồn nào để hỏi số liệu: danh sách tải xong (được hay hỏng), hoặc không có quyền đọc nguồn. */
  sanSang: boolean;
  dangTai: boolean;
  /** Nguồn đang xem nhận ghi. Không biết (không đọc được danh sách) thì coi là nhận — server là chốt cuối. */
  ghiDuoc: boolean;
  datNguon: (code: string) => void;
}

/**
 * Nguồn đang xem, giữ trên URL (`?nguon=<code>`) để F5, dán link và nút Back ra đúng nguồn. `replace` chứ
 * không `push`: đổi nguồn không phải một bước điều hướng.
 *
 * Mã trên URL chỉ dùng khi có thật trong danh sách; mã lạ hay vắng thì rơi về nguồn mặc định (suy ra lúc
 * vẽ, không viết đè URL bằng effect). Người không có `bao-cao:nguon:read` không đọc được danh sách: khi đó
 * mã trên URL đi thẳng xuống API (mã lạ thì server trả 404), vắng thì là nguồn mặc định.
 */
export function useNguon(): NguonDangXem {
  const duocDoc = useModulePermission(QUYEN.nguonDoc);
  const [thamSo, datThamSo] = useSearchParams();
  const q = useDanhSachNguon(duocDoc);

  const danhSach = q.data ?? [];
  const tuUrl = thamSo.get(THAM_SO_NGUON) ?? undefined;
  const khopUrl = tuUrl === undefined ? undefined : danhSach.find((n) => n.code === tuUrl);
  const macDinh = danhSach.find((n) => n.isDefault) ?? danhSach[0];
  const nguon = khopUrl ?? macDinh;
  const coDanhSach = duocDoc && q.isSuccess;

  const datNguon = useCallback(
    (code: string) => {
      datThamSo(
        (cu) => {
          const moi = new URLSearchParams(cu);
          moi.set(THAM_SO_NGUON, code);
          moi.delete(THAM_SO_THU_MUC);
          return moi;
        },
        { replace: true },
      );
    },
    [datThamSo],
  );

  return {
    code: coDanhSach ? nguon?.code : tuUrl,
    nguon: coDanhSach ? nguon : undefined,
    danhSach,
    sanSang: !duocDoc || !q.isPending,
    dangTai: duocDoc && q.isPending,
    ghiDuoc: !coDanhSach || nguon === undefined || nguon.isActive,
    datNguon,
  };
}

function useLamMoiNguon() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: KHOA_BAO_CAO.nguon });
}

export function useTaoNguon() {
  const lamMoi = useLamMoiNguon();
  return useMutation<Source, ApiError, CreateSourceInput>({ mutationFn: (du) => repo.taoNguon(du), onSuccess: lamMoi });
}

export function useSuaNguon() {
  const lamMoi = useLamMoiNguon();
  return useMutation<Source, ApiError, { id: string; du: UpdateSourceInput }>({
    mutationFn: ({ id, du }) => repo.suaNguon(id, du),
    onSuccess: lamMoi,
  });
}

/**
 * Đổi nguồn mặc định. Làm mới cả số liệu lẫn tài liệu: mọi lời gọi không kèm `?source=` nay trỏ sang nguồn
 * khác, nên cache cũ thuộc về nguồn khác.
 */
export function useDatNguonMacDinh() {
  const qc = useQueryClient();
  return useMutation<Source, ApiError, string>({
    mutationFn: (id) => repo.datNguonMacDinh(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KHOA_BAO_CAO.goc }),
  });
}

export function useDatTrangThaiNguon() {
  const lamMoi = useLamMoiNguon();
  return useMutation<Source, ApiError, { id: string; bat: boolean }>({
    mutationFn: ({ id, bat }) => repo.datTrangThaiNguon(id, bat),
    onSuccess: lamMoi,
  });
}
